import dayjs, { Dayjs } from 'dayjs';

export type WorkingStatus =
  | 'COMPLETED'
  | 'ONGOING'
  | 'MISSING_CHECKOUT'
  | 'N/A';

/** Shared labels for sidebar summary and calendar event chips */
export const ATTENDANCE_EVENT_LABELS: Record<string, string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  LEAVE: 'Leave',
  LEAVE_NO_INFO: 'Leave (no info)',
  HOLIDAY: 'Holiday',
  SUNDAY: 'Sunday',
  LATE_COMING: 'Late in',
  EARLY_GOING: 'Early out',
  HALF_DAY: 'Half day',
  LATE_CHECK_IN: 'Late Check-In',
  WEEKLY_OFF: 'Weekly Off',
};

export const getAttendanceEventLabel = (
  type: string,
  apiLabel?: string,
): string => {
  if (type === 'CHECK_IN' || type === 'CHECK_OUT') {
    return apiLabel || type;
  }

  // Named holidays keep their name (e.g. "Christmas Day") — the generic
  // "Holiday" chip already appears via computedStatus, and the span pill
  // only survives dedup because it has a name to show.
  if (type === 'HOLIDAY' && apiLabel && apiLabel !== 'Holiday') {
    return apiLabel;
  }

  return ATTENDANCE_EVENT_LABELS[type] || apiLabel || type;
};

export const ATTENDANCE_TYPE_COLORS: Record<string, string> = {
  PRESENT: '#22C55E',
  ABSENT: '#EF4444',
  LEAVE: '#F59E0B',
  HOLIDAY: '#60A5FA',
  CHECK_IN: '#22C55E',
  CHECK_OUT: '#EF4444',
  LATE_COMING: '#1E3A8A',
  EARLY_GOING: '#38BDF8',
  HALF_DAY: '#64748B',
  SUNDAY: '#3B82F6',
  LEAVE_NO_INFO: '#B45309',
  LATE_CHECK_IN: '#F97316',
  WEEKLY_OFF: '#3B82F6',
};

/** LEAVE day with no approved request and no reason on file → LEAVE_NO_INFO for display */
export const resolveDayDisplayStatus = (
  status?: string | null,
  events?: { type: string }[] | null,
): string | null | undefined =>
  status === 'LEAVE' && events?.some((e) => e.type === 'LEAVE_NO_INFO')
    ? 'LEAVE_NO_INFO'
    : status;

export const getComputedStatusLabel = (status?: string | null): string =>
  status ? ATTENDANCE_EVENT_LABELS[status] || status : '—';

export const getComputedStatusColor = (status?: string | null): string =>
  (status && ATTENDANCE_TYPE_COLORS[status]) || '#6b7280';

export const formatAttendanceClock = (time?: string | null): string => {
  if (!time) return '—';
  const parsed = dayjs(time);
  return parsed.isValid() ? parsed.format('hh:mm A') : '—';
};

/** Date + time; shows checkout date when it falls on a different day than the attendance day. */
export const formatAttendanceDateTime = (
  time?: string | null,
  attendanceDay?: string | Dayjs | Date | null,
): string => {
  if (!time) return '—';
  const parsed = dayjs(time);
  if (!parsed.isValid()) return '—';

  const dayRef = attendanceDay ? dayjs(attendanceDay) : null;
  const sameDay =
    !dayRef?.isValid() || parsed.format('YYYY-MM-DD') === dayRef.format('YYYY-MM-DD');

  if (sameDay) {
    return parsed.format('DD MMM, hh:mm A');
  }

  return parsed.format('DD MMM YYYY, hh:mm A');
};

export const formatWorkingMinutes = (
  minutes?: number | null,
  workingTime?: number | string | null,
): string => {
  if (workingTime === 'ONGOING') return 'In progress';
  if (workingTime === 'MISSING_CHECKOUT') return 'Missing checkout';
  if (workingTime === 'N/A' || workingTime == null) {
    if (minutes == null) return '—';
  }

  const value =
    typeof workingTime === 'number'
      ? workingTime
      : typeof minutes === 'number'
        ? minutes
        : null;

  if (value == null || Number.isNaN(value)) return '—';

  const hours = Math.floor(value / 60);
  const mins = value % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

export const resolveWorkingStatus = (params: {
  type?: string;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  attendanceDate?: string | Dayjs | Date | null;
  workingStatus?: WorkingStatus | string | null;
}): WorkingStatus => {
  if (params.workingStatus && params.workingStatus !== 'N/A') {
    return params.workingStatus as WorkingStatus;
  }

  const { type, checkInTime, checkOutTime, attendanceDate } = params;
  if (type !== 'PRESENT') return 'N/A';
  if (!checkInTime) return 'N/A';
  if (checkOutTime) return 'COMPLETED';

  const day = attendanceDate ? dayjs(attendanceDate) : null;
  if (day?.isValid() && day.isSame(dayjs(), 'day')) return 'ONGOING';
  return 'MISSING_CHECKOUT';
};

export const workingStatusLabel: Record<WorkingStatus, string> = {
  COMPLETED: 'Completed',
  ONGOING: 'On duty',
  MISSING_CHECKOUT: 'No checkout',
  'N/A': '—',
};

export const workingStatusColor: Record<WorkingStatus, string> = {
  COMPLETED: '#22C55E',
  ONGOING: '#F59E0B',
  MISSING_CHECKOUT: '#EF4444',
  'N/A': '#6b7280',
};
